// ** Type Imports
import { type MyPlan } from '@/entities/custom-pricing'
import { type PlanInterval } from '@/types'

// The product only sells in USD, so this only kicks in when the API omits it.
const FALLBACK_CURRENCY = 'USD'

const INTERVAL_LABELS: Record<PlanInterval, string> = {
	year: 'per year',
	month: 'per month'
}

/**
 * Formats an amount in the smallest currency unit, e.g. 99700 → "$997".
 * Cents are only shown when the amount actually has them.
 */
export const formatPlanAmount = (
	cents: number,
	currency: string | null
): string => {
	const fractionDigits = cents % 100 === 0 ? 0 : 2

	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: (currency ?? FALLBACK_CURRENCY).toUpperCase(),
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits
	}).format(cents / 100)
}

export const getIntervalLabel = (
	interval: PlanInterval | null
): string | null => (interval !== null ? INTERVAL_LABELS[interval] : null)

/**
 * The plan's checklist. The API's `features` win; when it sends none, bullets
 * are generated from the plan's numbers. Custom plans never get bullets — their
 * agreed terms are described by `customPlanNote` instead.
 */
export const getPlanFeatures = (plan: MyPlan): string[] => {
	if (plan.isCustom) return []
	if (plan.features.length > 0) return plan.features

	const features: string[] = []

	if (plan.includedClients !== null) {
		features.push(`Manage up to ${plan.includedClients} clients included`)
	}

	if (plan.additionalClientAmount !== null) {
		const amount = formatPlanAmount(plan.additionalClientAmount, plan.currency)
		const suffix = plan.interval === 'year' ? ' per year' : ''

		features.push(`${amount} per additional client${suffix}`)
	}

	return features
}
